import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Hero from './sections/Hero';
import News from './sections/News';
import About from './sections/About';
import Service from './sections/Service';
import Vision from './sections/Vision';
import Value from './sections/Value';
import CompanyRecruit from './sections/CompanyRecruit';
import Contact from './sections/Contact';
import LanguageProvider from './contexts/LanguageContext';
import { useMetadata } from './hooks/useMetadata';
import { useRecaptchaBadge } from './hooks/useRecaptchaBadge';
import useSmoothScroll from './hooks/useSmoothScroll';
import StructuredData from './components/StructuredData';
import FadeInSection from './components/FadeInSection';
import Raid2026TopCta from './components/Raid2026TopCta';
import StgAuthGate from './components/StgAuthGate';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Raid2026 from './pages/Raid2026';
import NewsIndex from './pages/NewsIndex';
import NewsDetail from './pages/NewsDetail';
import { isRaid2026CorporateNavPublished } from './config/raid2026';
import { EngineeringTopCta } from './components/EngineeringTopCta';
import EngineeringPageTransition from './components/EngineeringPageTransition';
import { loadEngineeringPage } from './routes/engineeringPage';
import DevViewportIndicator from './components/DevViewportIndicator';
// Entry production release is explicit; ordinary production builds stay unchanged.
const ProductEngineering = React.lazy(loadEngineeringPage);

// Keep these build-time conditions inline so webpack excludes Entry chunks
// entirely from production; a runtime route gate alone still emits the bundle.
const EngineeringEntry =
  (process.env.REACT_APP_SITE_ENV === 'production' &&
    process.env.REACT_APP_ENGINEERING_ENTRY_ENABLED === 'true') ||
  process.env.REACT_APP_SITE_ENV === 'stg' ||
  process.env.REACT_APP_SITE_ENV === 'preview' ||
  process.env.REACT_APP_SITE_ENV === 'development' ||
  (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SITE_ENV)
    ? React.lazy(() => import('./pages/EngineeringEntry'))
    : null;
const EngineeringEntryThanks =
  (process.env.REACT_APP_SITE_ENV === 'production' &&
    process.env.REACT_APP_ENGINEERING_ENTRY_ENABLED === 'true') ||
  process.env.REACT_APP_SITE_ENV === 'stg' ||
  process.env.REACT_APP_SITE_ENV === 'preview' ||
  process.env.REACT_APP_SITE_ENV === 'development' ||
  (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SITE_ENV)
    ? React.lazy(() => import('./pages/EngineeringEntryThanks'))
    : null;

const isLocalDev = process.env.NODE_ENV === 'development';

function AppContent() {
  const intl = useIntl();
  const { setPageMetadata } = useMetadata();
  const showRaid2026Cta =
    intl.locale === 'ja' && isRaid2026CorporateNavPublished();

  // heroセクションが表示されているかどうかを監視
  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    rootMargin: '0px',
  });

  // 初期メタデータを設定
  useEffect(() => {
    setPageMetadata(); // デフォルトメタデータを設定
  }, [setPageMetadata]);

  // reCAPTCHAバッジの制御
  useRecaptchaBadge(heroInView);

  return (
    <>
      <StructuredData />
      <div className="min-h-screen flex flex-col font-body">
        <Header />
        <main className="flex-grow">
          <div ref={heroRef}>
            <Hero id="hero-section" />
          </div>
          {intl.locale === 'ja' ? (
            <EngineeringTopCta />
          ) : (
            showRaid2026Cta && <Raid2026TopCta />
          )}
          <FadeInSection id="news">
            <News />
          </FadeInSection>
          <FadeInSection id="about">
            <About />
          </FadeInSection>
          <FadeInSection id="service">
            <Service />
          </FadeInSection>
          <FadeInSection id="vision">
            <Vision />
          </FadeInSection>
          <FadeInSection id="value">
            <Value />
          </FadeInSection>
          <FadeInSection id="company-recruit">
            <CompanyRecruit />
          </FadeInSection>
          <FadeInSection id="contact">
            <Contact />
          </FadeInSection>
        </main>
        <Footer />
      </div>
    </>
  );
}

function App() {
  useSmoothScroll();

  return (
    <BrowserRouter>
      <LanguageProvider>
        <StgAuthGate>
          <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/news" element={<NewsIndex />} />
            <Route path="/news/:contentId" element={<NewsDetail />} />
            <Route path="/recruit/raid2026" element={<Raid2026 />} />
            {ProductEngineering && (
              <>
                <Route
                  path="/recruit/engineering"
                  element={
                    <EngineeringPageTransition>
                      <ProductEngineering />
                    </EngineeringPageTransition>
                  }
                />
                <Route
                  path="/recruit/product-engineering"
                  element={<Navigate to="/recruit/engineering" replace />}
                />
              </>
            )}
            {EngineeringEntry && (
              <>
                <Route
                  path="/recruit/engineering/entry"
                  element={
                    <EngineeringPageTransition>
                      <EngineeringEntry />
                    </EngineeringPageTransition>
                  }
                />
                <Route
                  path="/recruit/engineering/entry/thanks"
                  element={
                    <EngineeringPageTransition>
                      <EngineeringEntryThanks />
                    </EngineeringPageTransition>
                  }
                />
              </>
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </StgAuthGate>
      </LanguageProvider>
      {isLocalDev && <DevViewportIndicator />}
    </BrowserRouter>
  );
}

export default App;
